import { useEffect } from 'react';

import { Input, InputNumber, Select, Col, Row } from 'antd';

import { useParams } from 'react-router-dom';

import classnames from 'classnames';

import {
  WithLoading,
  Form,
  WrapperSelect,
  SectionWrapper,
  CustomNotification,
} from 'common/components';
import {
  SnapshotInputWrapper,
  SnapshotFormItemWrapper,
  FormLabel,
} from 'pages/qa-spec/components/form';

import {
  EDIT_SECTION,
  EditTabSectionWrapper,
} from 'pages/qa-spec/components/edit-tab-checker';

import EditContainer from '../container/EditContainer';

import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';

import { useCheckSnapshotForRetailer } from 'pages/qa-spec/hooks';
import { useGetEnumsByEnumName } from 'hooks';
import {
  useGetFreshDeli,
  useSaveFreshDeli,
  useQueryQaFreshDeliSnapshot,
} from './hooks';

import { useGetSnapshotFormItemWrapperProps } from 'pages/qa-spec/components/form/hooks';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from 'pages/qa-spec/hooks/useGetQaSpecRequiredFields';

import './QaFreshDeli.less';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import { getFormattedPath } from '../shared/QaSpecHelper';

const SMOKE_TYPE_ENUM = 'SmokeTypeEnum';
const RENNET_ENUM = 'RennetEnum';
const PASTEURIZING_METHOD_ENUM = 'PasteurizingMethodEnum';
const YES_NO_ENUM = ['Yes', 'No'];

const NAME_FORM_MEAL = 'qaSpecFreshDeliMeat';
const NAME_FORM_CHEESE = 'qaSpecFreshDeliCheese';

const QaFreshDeli = () => {
  const [formInstance] = Form.useForm();

  const { id: productId } = useParams();

  const smokeTypeEnum = useGetEnumsByEnumName(SMOKE_TYPE_ENUM);
  const rennetEnum = useGetEnumsByEnumName(RENNET_ENUM);
  const pasteurizingMethodEnumEnum = useGetEnumsByEnumName(
    PASTEURIZING_METHOD_ENUM
  );

  const useSnapshot = useCheckSnapshotForRetailer();

  const { data: freshDeliData, isLoading } = useGetFreshDeli(productId);
  const { data: freshDeliSnapshotData } = useQueryQaFreshDeliSnapshot({
    productId,
    enabled: Boolean(productId && useSnapshot),
  });
  const freshDeliMutation = useSaveFreshDeli(productId);

  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });

  const { getSnapshotFormItemWrapperProps } =
    useGetSnapshotFormItemWrapperProps({
      snapshotValues: freshDeliSnapshotData,
    });

  const handleSave = async (toggleEditMode) => {
    try {
      const formValues = await formInstance.validateFields();
      const params = {
        productId,
        ...formValues,
      };

      freshDeliMutation.mutate(params, { onSuccess: () => toggleEditMode() });
    } catch (error) {
      CustomNotification.error('Please resolve error(s)');
    }
  };

  const handleCancel = () => {
    formInstance.resetFields();
    formInstance.setFieldsValue({ ...freshDeliData });
  };

  const checkQasRequiredField = (fieldName) => {
    if (!requiredFields?.length) return false;

    const isRequired = requiredFields.includes(
      `qaSpecification.qaSpecFreshDeli.${fieldName}`.toLowerCase()
    );

    return isRequired;
  };

  const getRequiredRule = ({ fieldName = [], displayName }) => {
    const isQasRequiredField = checkQasRequiredField(fieldName.join('.'));
    return {
      required: isQasRequiredField,
      message: `${displayName || 'This field'} is required`,
    };
  };

  useEffect(() => {
    if (freshDeliData) {
      formInstance.setFieldsValue({ ...freshDeliData });
    }
  }, [formInstance, freshDeliData]);

  return (
    <Form
      className='qa-fresh-deli'
      form={formInstance}
      style={{ paddingRight: 5 }}
      labelCol={{ flex: '166px' }}
    >
      <WithLoading loading={isLoading || freshDeliMutation.isLoading}>
        <EditContainer
          type={EDIT_SECTION.PROD_FRESH_DELI}
          productId={productId}
          saveLoading={freshDeliMutation.isLoading}
          handleSave={handleSave}
          handleCancel={handleCancel}
          float
        >
          {(toggleEditMode) => {
            const disabled = !toggleEditMode;

            return (
              <EditTabSectionWrapper.CheckerWrapper
                isEdit={toggleEditMode}
                type={EDIT_SECTION.PROD_FRESH_DELI}
              >
                <Row
                  gutter={[0, 12]}
                  className={classnames('qa-fresh-deli__input', {
                    'qa-fresh-deli__input--edit': toggleEditMode,
                  })}
                >
                  <Col span={24}>
                    <SectionWrapper title='Meat'>
                      <Row gutter={[24, 12]}>
                        <Col span={10}>
                          <SnapshotFormItemWrapper
                            {...getSnapshotFormItemWrapperProps({
                              fieldPath: [NAME_FORM_MEAL, 'proteinType'],
                              label: 'Protein Type',
                            })}
                          >
                            {(inputWrapperProps) => (
                              <Form.Item
                                name={[NAME_FORM_MEAL, 'proteinType']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={getFormattedPath(
                                      'qaSpecification.freshDeli.meat.proteinType'
                                    )}
                                    qaSpecFieldName='Protein Type'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.meat.proteinType'>
                                      <FormLabel label='Protein Type' />
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                rules={[
                                  getRequiredRule({
                                    fieldName: [NAME_FORM_MEAL, 'proteinType'],
                                    displayName: 'Protein Type',
                                  }),
                                ]}
                              >
                                <SnapshotInputWrapper
                                  {...inputWrapperProps}
                                  popoverProps={{ visible: false }}
                                >
                                  <Input readOnly={disabled} />
                                </SnapshotInputWrapper>
                              </Form.Item>
                            )}
                          </SnapshotFormItemWrapper>
                          <SnapshotFormItemWrapper
                            {...getSnapshotFormItemWrapperProps({
                              fieldPath: [NAME_FORM_MEAL, 'protein'],
                              label: 'Protein %',
                            })}
                          >
                            {(inputWrapperProps) => (
                              <Form.Item
                                name={[NAME_FORM_MEAL, 'protein']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={getFormattedPath(
                                      'qaSpecification.freshDeli.meat.protein'
                                    )}
                                    qaSpecFieldName='Protein %'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.meat.protein'>
                                      <FormLabel label='Protein %' />
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                rules={[
                                  getRequiredRule({
                                    fieldName: [NAME_FORM_MEAL, 'protein'],
                                    displayName: 'Protein %',
                                  }),
                                ]}
                              >
                                <SnapshotInputWrapper
                                  {...inputWrapperProps}
                                  popoverProps={{ visible: false }}
                                >
                                  <InputNumber
                                    min={0}
                                    readOnly={disabled}
                                    style={{ width: '100%' }}
                                  />
                                </SnapshotInputWrapper>
                              </Form.Item>
                            )}
                          </SnapshotFormItemWrapper>

                          <SnapshotFormItemWrapper
                            {...getSnapshotFormItemWrapperProps({
                              fieldPath: [NAME_FORM_MEAL, 'smoked'],
                              label: 'Smoked',
                            })}
                          >
                            {(inputWrapperProps) => (
                              <Form.Item
                                name={[NAME_FORM_MEAL, 'smoked']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={getFormattedPath(
                                      'qaSpecification.freshDeli.meat.smoked'
                                    )}
                                    qaSpecFieldName='Smoked'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.meat.smoked'>
                                      <FormLabel label='Smoked' />
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                rules={[
                                  getRequiredRule({
                                    fieldName: [NAME_FORM_MEAL, 'smoked'],
                                    displayName: 'Smoked',
                                  }),
                                ]}
                              >
                                <SnapshotInputWrapper
                                  {...inputWrapperProps}
                                  popoverProps={{ visible: false }}
                                >
                                  <WrapperSelect
                                    listHeight={140}
                                    disabled={disabled}
                                  >
                                    {YES_NO_ENUM.map((value) => (
                                      <Select.Option key={value} value={value}>
                                        {value}
                                      </Select.Option>
                                    ))}
                                  </WrapperSelect>
                                </SnapshotInputWrapper>
                              </Form.Item>
                            )}
                          </SnapshotFormItemWrapper>
                          <SnapshotFormItemWrapper
                            {...getSnapshotFormItemWrapperProps({
                              fieldPath: [NAME_FORM_MEAL, 'smokeType'],
                              label: 'Smoke Type',
                            })}
                          >
                            {(inputWrapperProps) => (
                              <Form.Item
                                name={[NAME_FORM_MEAL, 'smokeType']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={getFormattedPath(
                                      'qaSpecification.freshDeli.meat.smokeType'
                                    )}
                                    qaSpecFieldName='Smoke Type'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.meat.smokeType'>
                                      <FormLabel label='Smoke Type' />
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                rules={[
                                  getRequiredRule({
                                    fieldName: [NAME_FORM_MEAL, 'smokeType'],
                                    displayName: 'Smoke Type',
                                  }),
                                ]}
                              >
                                <SnapshotInputWrapper
                                  {...inputWrapperProps}
                                  popoverProps={{ visible: false }}
                                >
                                  <WrapperSelect disabled={disabled}>
                                    {smokeTypeEnum.map(
                                      ({ enumDisplayName }) => (
                                        <Select.Option
                                          key={enumDisplayName}
                                          value={enumDisplayName}
                                        >
                                          {enumDisplayName}
                                        </Select.Option>
                                      )
                                    )}
                                  </WrapperSelect>
                                </SnapshotInputWrapper>
                              </Form.Item>
                            )}
                          </SnapshotFormItemWrapper>
                        </Col>
                        <Col span={14}>
                          <SnapshotFormItemWrapper
                            {...getSnapshotFormItemWrapperProps({
                              fieldPath: [NAME_FORM_MEAL, 'composition'],
                              label:
                                'Composition (Ex.  Chopped and Formed, Whole Muscle, etc)',
                            })}
                          >
                            {(inputWrapperProps) => (
                              <Form.Item
                                name={[NAME_FORM_MEAL, 'composition']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={getFormattedPath(
                                      'qaSpecification.freshDeli.meat.composition'
                                    )}
                                    qaSpecFieldName='Composition (Ex.  Chopped and Formed, Whole Muscle, etc)'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.meat.composition'>
                                      <FormLabel label='Composition (Ex.  Chopped and Formed, Whole Muscle, etc)' />
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                rules={[
                                  getRequiredRule({
                                    fieldName: [NAME_FORM_MEAL, 'composition'],
                                    displayName: 'Composition',
                                  }),
                                ]}
                              >
                                <SnapshotInputWrapper
                                  {...inputWrapperProps}
                                  popoverProps={{ visible: false }}
                                >
                                  <Input.TextArea
                                    autoSize={{ minRows: 4, maxRows: 5 }}
                                    maxLength={500}
                                    showCount
                                    readOnly={disabled}
                                  />
                                </SnapshotInputWrapper>
                              </Form.Item>
                            )}
                          </SnapshotFormItemWrapper>
                        </Col>
                      </Row>
                    </SectionWrapper>
                  </Col>
                  <Col span={24}>
                    <SectionWrapper title='Cheese'>
                      <Row gutter={[24, 12]}>
                        <Col span={10}>
                          <SnapshotFormItemWrapper
                            {...getSnapshotFormItemWrapperProps({
                              fieldPath: [NAME_FORM_CHEESE, 'cheeseType'],
                              label: 'Cheese Type',
                            })}
                          >
                            {(inputWrapperProps) => (
                              <Form.Item
                                name={[NAME_FORM_CHEESE, 'cheeseType']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={getFormattedPath(
                                      'qaSpecification.freshDeli.cheese.cheeseType'
                                    )}
                                    qaSpecFieldName='Cheese Type'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.cheeseType'>
                                      <FormLabel label='Cheese Type' />
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                rules={[
                                  getRequiredRule({
                                    fieldName: [NAME_FORM_CHEESE, 'cheeseType'],
                                    displayName: 'Cheese Type',
                                  }),
                                ]}
                              >
                                <SnapshotInputWrapper
                                  {...inputWrapperProps}
                                  popoverProps={{ visible: false }}
                                >
                                  <Input.TextArea
                                    autoSize={{ minRows: 4, maxRows: 5 }}
                                    readOnly={disabled}
                                    maxLength={500}
                                    showCount
                                  />
                                </SnapshotInputWrapper>
                              </Form.Item>
                            )}
                          </SnapshotFormItemWrapper>

                          <SnapshotFormItemWrapper
                            {...getSnapshotFormItemWrapperProps({
                              fieldPath: [NAME_FORM_CHEESE, 'milkType'],
                              label: 'Milk Type',
                            })}
                          >
                            {(inputWrapperProps) => (
                              <Form.Item
                                name={[NAME_FORM_CHEESE, 'milkType']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={getFormattedPath(
                                      'qaSpecification.freshDeli.cheese.milkType'
                                    )}
                                    qaSpecFieldName='Milk Type'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.milkType'>
                                      <FormLabel label='Milk Type' />
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                rules={[
                                  getRequiredRule({
                                    fieldName: [NAME_FORM_CHEESE, 'milkType'],
                                    displayName: 'Milk Type',
                                  }),
                                ]}
                              >
                                <SnapshotInputWrapper
                                  {...inputWrapperProps}
                                  popoverProps={{ visible: false }}
                                >
                                  <Input.TextArea
                                    autoSize={{ minRows: 4, maxRows: 5 }}
                                    readOnly={disabled}
                                    maxLength={500}
                                    showCount
                                  />
                                </SnapshotInputWrapper>
                              </Form.Item>
                            )}
                          </SnapshotFormItemWrapper>
                          <SnapshotFormItemWrapper
                            {...getSnapshotFormItemWrapperProps({
                              fieldPath: [NAME_FORM_CHEESE, 'processFlows'],
                              label: 'Process Flows',
                            })}
                          >
                            {(inputWrapperProps) => (
                              <Form.Item
                                name={[NAME_FORM_CHEESE, 'processFlows']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={getFormattedPath(
                                      'qaSpecification.freshDeli.cheese.processFlows'
                                    )}
                                    qaSpecFieldName='Process Flows'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.processFlows'>
                                      <FormLabel label='Process Flows' />
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                rules={[
                                  getRequiredRule({
                                    fieldName: [
                                      NAME_FORM_CHEESE,
                                      'processFlows',
                                    ],
                                    displayName: 'Process Flows',
                                  }),
                                ]}
                              >
                                <SnapshotInputWrapper
                                  {...inputWrapperProps}
                                  popoverProps={{ visible: false }}
                                >
                                  <Input.TextArea
                                    autoSize={{ minRows: 4, maxRows: 5 }}
                                    readOnly={disabled}
                                    maxLength={500}
                                    showCount
                                  />
                                </SnapshotInputWrapper>
                              </Form.Item>
                            )}
                          </SnapshotFormItemWrapper>
                          <SnapshotFormItemWrapper
                            {...getSnapshotFormItemWrapperProps({
                              fieldPath: [NAME_FORM_CHEESE, 'agingDetails'],
                              label: 'Aging Details',
                            })}
                          >
                            {(inputWrapperProps) => (
                              <Form.Item
                                name={[NAME_FORM_CHEESE, 'agingDetails']}
                                label={
                                  <TraceableCommentContainer
                                    qaSpecName={getFormattedPath(
                                      'qaSpecification.freshDeli.cheese.agingDetails'
                                    )}
                                    qaSpecFieldName='Aging Details'
                                  >
                                    <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.agingDetails'>
                                      <FormLabel label='Aging Details' />
                                    </QaSpecHelpContentLabel>
                                  </TraceableCommentContainer>
                                }
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                rules={[
                                  getRequiredRule({
                                    fieldName: [
                                      NAME_FORM_CHEESE,
                                      'agingDetails',
                                    ],
                                    displayName: 'Aging Details',
                                  }),
                                ]}
                              >
                                <SnapshotInputWrapper
                                  {...inputWrapperProps}
                                  popoverProps={{ visible: false }}
                                >
                                  <Input.TextArea
                                    autoSize={{ minRows: 4, maxRows: 5 }}
                                    readOnly={disabled}
                                    maxLength={500}
                                    showCount
                                  />
                                </SnapshotInputWrapper>
                              </Form.Item>
                            )}
                          </SnapshotFormItemWrapper>
                        </Col>
                        <Col span={14}>
                          <Row>
                            <Col span={12}>
                              <SnapshotFormItemWrapper
                                {...getSnapshotFormItemWrapperProps({
                                  fieldPath: [NAME_FORM_CHEESE, 'rind'],
                                  label: 'Rind',
                                })}
                              >
                                {(inputWrapperProps) => (
                                  <Form.Item
                                    name={[NAME_FORM_CHEESE, 'rind']}
                                    label={
                                      <TraceableCommentContainer
                                        qaSpecName={getFormattedPath(
                                          'qaSpecification.freshDeli.cheese.rind'
                                        )}
                                        qaSpecFieldName='Rind'
                                      >
                                        <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.rind'>
                                          <FormLabel label='Rind' />
                                        </QaSpecHelpContentLabel>
                                      </TraceableCommentContainer>
                                    }
                                    rules={[
                                      getRequiredRule({
                                        fieldName: [NAME_FORM_CHEESE, 'rind'],
                                        displayName: 'Rind',
                                      }),
                                    ]}
                                  >
                                    <SnapshotInputWrapper
                                      {...inputWrapperProps}
                                      popoverProps={{ visible: false }}
                                    >
                                      <WrapperSelect
                                        listHeight={140}
                                        disabled={disabled}
                                      >
                                        {YES_NO_ENUM.map((value) => (
                                          <Select.Option
                                            key={value}
                                            value={value}
                                          >
                                            {value}
                                          </Select.Option>
                                        ))}
                                      </WrapperSelect>
                                    </SnapshotInputWrapper>
                                  </Form.Item>
                                )}
                              </SnapshotFormItemWrapper>
                            </Col>
                            <Col span={12}>
                              <SnapshotFormItemWrapper
                                {...getSnapshotFormItemWrapperProps({
                                  fieldPath: [NAME_FORM_CHEESE, 'rindEdible'],
                                  label: 'Rind Edible',
                                })}
                              >
                                {(inputWrapperProps) => (
                                  <Form.Item
                                    name={[NAME_FORM_CHEESE, 'rindEdible']}
                                    label={
                                      <TraceableCommentContainer
                                        qaSpecName={getFormattedPath(
                                          'qaSpecification.freshDeli.cheese.rindEdible'
                                        )}
                                        qaSpecFieldName='Rind Edible'
                                      >
                                        <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.rindEdible'>
                                          <FormLabel label='Rind Edible' />
                                        </QaSpecHelpContentLabel>
                                      </TraceableCommentContainer>
                                    }
                                    rules={[
                                      getRequiredRule({
                                        fieldName: [
                                          NAME_FORM_CHEESE,
                                          'rindEdible',
                                        ],
                                        displayName: 'Rind Edible',
                                      }),
                                    ]}
                                  >
                                    <SnapshotInputWrapper
                                      {...inputWrapperProps}
                                      popoverProps={{ visible: false }}
                                    >
                                      <WrapperSelect
                                        listHeight={140}
                                        disabled={disabled}
                                      >
                                        {YES_NO_ENUM.map((value) => (
                                          <Select.Option
                                            key={value}
                                            value={value}
                                          >
                                            {value}
                                          </Select.Option>
                                        ))}
                                      </WrapperSelect>
                                    </SnapshotInputWrapper>
                                  </Form.Item>
                                )}
                              </SnapshotFormItemWrapper>
                            </Col>
                            <Col span={12}>
                              <SnapshotFormItemWrapper
                                {...getSnapshotFormItemWrapperProps({
                                  fieldPath: [NAME_FORM_CHEESE, 'ccp'],
                                  label: 'CCP (Critical Control Point)',
                                })}
                              >
                                {(inputWrapperProps) => (
                                  <Form.Item
                                    name={[NAME_FORM_CHEESE, 'ccp']}
                                    label={
                                      <TraceableCommentContainer
                                        qaSpecName={getFormattedPath(
                                          'qaSpecification.freshDeli.cheese.ccp'
                                        )}
                                        qaSpecFieldName='CCP (Critical Control Point)'
                                      >
                                        <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.ccp'>
                                          <FormLabel
                                            label='CCP (Critical Control Point)'
                                            ellipsis={{
                                              tooltip:
                                                'CCP (Critical Control Point)',
                                            }}
                                          />
                                        </QaSpecHelpContentLabel>
                                      </TraceableCommentContainer>
                                    }
                                    rules={[
                                      getRequiredRule({
                                        fieldName: [NAME_FORM_CHEESE, 'ccp'],
                                        displayName: 'CCP',
                                      }),
                                    ]}
                                  >
                                    <SnapshotInputWrapper
                                      {...inputWrapperProps}
                                      popoverProps={{ visible: false }}
                                    >
                                      <WrapperSelect
                                        listHeight={140}
                                        disabled={disabled}
                                      >
                                        {YES_NO_ENUM.map((value) => (
                                          <Select.Option
                                            key={value}
                                            value={value}
                                          >
                                            {value}
                                          </Select.Option>
                                        ))}
                                      </WrapperSelect>
                                    </SnapshotInputWrapper>
                                  </Form.Item>
                                )}
                              </SnapshotFormItemWrapper>
                            </Col>
                            <Col span={12}>
                              <SnapshotFormItemWrapper
                                {...getSnapshotFormItemWrapperProps({
                                  fieldPath: [NAME_FORM_CHEESE, 'pasteurized'],
                                  label: 'Pasteurized',
                                })}
                              >
                                {(inputWrapperProps) => (
                                  <Form.Item
                                    name={[NAME_FORM_CHEESE, 'pasteurized']}
                                    label={
                                      <TraceableCommentContainer
                                        qaSpecName={getFormattedPath(
                                          'qaSpecification.freshDeli.cheese.pasteurized'
                                        )}
                                        qaSpecFieldName='Pasteurized'
                                      >
                                        <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.pasteurized'>
                                          <FormLabel label='Pasteurized' />
                                        </QaSpecHelpContentLabel>
                                      </TraceableCommentContainer>
                                    }
                                    rules={[
                                      getRequiredRule({
                                        fieldName: [
                                          NAME_FORM_CHEESE,
                                          'pasteurized',
                                        ],
                                        displayName: 'Pasteurized',
                                      }),
                                    ]}
                                  >
                                    <SnapshotInputWrapper
                                      {...inputWrapperProps}
                                      popoverProps={{ visible: false }}
                                    >
                                      <WrapperSelect
                                        listHeight={140}
                                        disabled={disabled}
                                      >
                                        {YES_NO_ENUM.map((value) => (
                                          <Select.Option
                                            key={value}
                                            value={value}
                                          >
                                            {value}
                                          </Select.Option>
                                        ))}
                                      </WrapperSelect>
                                    </SnapshotInputWrapper>
                                  </Form.Item>
                                )}
                              </SnapshotFormItemWrapper>
                            </Col>
                            <Col span={24}>
                              <SnapshotFormItemWrapper
                                {...getSnapshotFormItemWrapperProps({
                                  fieldPath: [NAME_FORM_CHEESE, 'rennet'],
                                  label: 'Rennet',
                                })}
                              >
                                {(inputWrapperProps) => (
                                  <Form.Item
                                    name={[NAME_FORM_CHEESE, 'rennet']}
                                    label={
                                      <TraceableCommentContainer
                                        qaSpecName={getFormattedPath(
                                          'qaSpecification.freshDeli.cheese.rennet'
                                        )}
                                        qaSpecFieldName='Rennet'
                                      >
                                        <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.rennet'>
                                          <FormLabel label='Rennet' />
                                        </QaSpecHelpContentLabel>
                                      </TraceableCommentContainer>
                                    }
                                    rules={[
                                      getRequiredRule({
                                        fieldName: [NAME_FORM_CHEESE, 'rennet'],
                                        displayName: 'Rennet',
                                      }),
                                    ]}
                                  >
                                    <SnapshotInputWrapper
                                      {...inputWrapperProps}
                                      popoverProps={{ visible: false }}
                                    >
                                      <WrapperSelect
                                        listHeight={140}
                                        disabled={disabled}
                                      >
                                        {rennetEnum.map(
                                          ({ enumDisplayName }) => (
                                            <Select.Option
                                              key={enumDisplayName}
                                              value={enumDisplayName}
                                            >
                                              {enumDisplayName}
                                            </Select.Option>
                                          )
                                        )}
                                      </WrapperSelect>
                                    </SnapshotInputWrapper>
                                  </Form.Item>
                                )}
                              </SnapshotFormItemWrapper>
                            </Col>
                            <Col span={24}>
                              <SnapshotFormItemWrapper
                                {...getSnapshotFormItemWrapperProps({
                                  fieldPath: [
                                    NAME_FORM_CHEESE,
                                    'pasteurizingMethod',
                                  ],
                                  label: 'Pasteurizing Method',
                                })}
                              >
                                {(inputWrapperProps) => (
                                  <Form.Item
                                    name={[
                                      NAME_FORM_CHEESE,
                                      'pasteurizingMethod',
                                    ]}
                                    label={
                                      <TraceableCommentContainer
                                        qaSpecName={getFormattedPath(
                                          'qaSpecification.freshDeli.cheese.pasteurizingMethod'
                                        )}
                                        qaSpecFieldName='Pasteurizing Method'
                                      >
                                        <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.pasteurizingMethod'>
                                          <FormLabel label='Pasteurizing Method' />
                                        </QaSpecHelpContentLabel>
                                      </TraceableCommentContainer>
                                    }
                                    rules={[
                                      getRequiredRule({
                                        fieldName: [
                                          NAME_FORM_CHEESE,
                                          'pasteurizingMethod',
                                        ],
                                        displayName: 'Pasteurizing Method',
                                      }),
                                    ]}
                                  >
                                    <SnapshotInputWrapper
                                      {...inputWrapperProps}
                                      popoverProps={{ visible: false }}
                                    >
                                      <WrapperSelect
                                        listHeight={140}
                                        disabled={disabled}
                                      >
                                        {pasteurizingMethodEnumEnum.map(
                                          ({ enumDisplayName }) => (
                                            <Select.Option
                                              key={enumDisplayName}
                                              value={enumDisplayName}
                                            >
                                              {enumDisplayName}
                                            </Select.Option>
                                          )
                                        )}
                                      </WrapperSelect>
                                    </SnapshotInputWrapper>
                                  </Form.Item>
                                )}
                              </SnapshotFormItemWrapper>
                            </Col>
                            <Col span={24}>
                              <SnapshotFormItemWrapper
                                {...getSnapshotFormItemWrapperProps({
                                  fieldPath: [
                                    NAME_FORM_CHEESE,
                                    'productionControls',
                                  ],
                                  label: 'Production Controls',
                                })}
                              >
                                {(inputWrapperProps) => (
                                  <Form.Item
                                    name={[
                                      NAME_FORM_CHEESE,
                                      'productionControls',
                                    ]}
                                    label={
                                      <TraceableCommentContainer
                                        qaSpecName={getFormattedPath(
                                          'qaSpecification.freshDeli.cheese.productionControls'
                                        )}
                                        qaSpecFieldName='Production Controls'
                                      >
                                        <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.productionControls'>
                                          <FormLabel label='Production Controls' />
                                        </QaSpecHelpContentLabel>
                                      </TraceableCommentContainer>
                                    }
                                    labelCol={{ span: 24 }}
                                    wrapperCol={{ span: 24 }}
                                    rules={[
                                      getRequiredRule({
                                        fieldName: [
                                          NAME_FORM_CHEESE,
                                          'productionControls',
                                        ],
                                        displayName: 'Production Controls',
                                      }),
                                    ]}
                                  >
                                    <SnapshotInputWrapper
                                      {...inputWrapperProps}
                                      popoverProps={{ visible: false }}
                                    >
                                      <Input.TextArea
                                        autoSize={{ minRows: 4, maxRows: 5 }}
                                        readOnly={disabled}
                                        maxLength={500}
                                        showCount
                                      />
                                    </SnapshotInputWrapper>
                                  </Form.Item>
                                )}
                              </SnapshotFormItemWrapper>
                            </Col>
                            <Col span={24}>
                              <SnapshotFormItemWrapper
                                {...getSnapshotFormItemWrapperProps({
                                  fieldPath: [
                                    NAME_FORM_CHEESE,
                                    'productionControls',
                                  ],
                                  label: 'Production Controls',
                                })}
                              >
                                {(inputProps) => (
                                  <Form.Item
                                    name={[NAME_FORM_CHEESE, 'qualityControls']}
                                    label={
                                      <TraceableCommentContainer
                                        qaSpecName={getFormattedPath(
                                          'qaSpecification.freshDeli.cheese.qualityControls'
                                        )}
                                        qaSpecFieldName='Quality Controls'
                                      >
                                        <QaSpecHelpContentLabel fieldPath='qaSpecification.freshDeli.cheese.qualityControls'>
                                          <FormLabel label='Quality Controls' />
                                        </QaSpecHelpContentLabel>
                                      </TraceableCommentContainer>
                                    }
                                    labelCol={{ span: 24 }}
                                    wrapperCol={{ span: 24 }}
                                    rules={[
                                      getRequiredRule({
                                        fieldName: [
                                          NAME_FORM_CHEESE,
                                          'qualityControls',
                                        ],
                                        displayName: 'Quality Controls',
                                      }),
                                    ]}
                                  >
                                    <SnapshotInputWrapper
                                      {...inputProps}
                                      popoverProps={{ visible: false }}
                                    >
                                      <Input.TextArea
                                        autoSize={{ minRows: 4, maxRows: 5 }}
                                        maxLength={500}
                                        showCount
                                        readOnly={disabled}
                                      />
                                    </SnapshotInputWrapper>
                                  </Form.Item>
                                )}
                              </SnapshotFormItemWrapper>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </SectionWrapper>
                  </Col>
                </Row>
              </EditTabSectionWrapper.CheckerWrapper>
            );
          }}
        </EditContainer>
      </WithLoading>
    </Form>
  );
};

export default QaFreshDeli;
